import { useMemo, useState } from "react";
import { Icon } from "../icons";
import type { Conversation } from "../types";
import type { VsmartAction, VsmartProposal } from "../integrationApi";

type QueryResult = Record<string, unknown>;

type Props = {
  conversation: Conversation;
  enabled: boolean;
  canReview: boolean;
  onQuery: (
    conversation: Conversation,
    action: VsmartAction,
    args?: Record<string, unknown>,
  ) => Promise<QueryResult>;
};

export function VsmartContextCard({ conversation, enabled, canReview, onQuery }: Props) {
  const [loading, setLoading] = useState<string | null>(null);
  const [result, setResult] = useState<{ action: VsmartAction; value: QueryResult } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [proposals, setProposals] = useState<VsmartProposal[]>([]);
  const [proposalsLoaded, setProposalsLoaded] = useState(false);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});

  const pendingCount = useMemo(
    () => proposals.filter((item) => item.status === "proposed" || item.status === "approved").length,
    [proposals],
  );

  async function run(action: VsmartAction, args: Record<string, unknown> = {}) {
    if (!enabled || loading) return;
    setLoading(action);
    setError(null);
    setNotice(null);
    try {
      setResult({ action, value: await onQuery(conversation, action, args) });
    } catch (cause) {
      setError(friendlyError(cause instanceof Error ? cause.message : "VSMART_QUERY_FAILED"));
    } finally {
      setLoading(null);
    }
  }

  async function loadProposals() {
    if (!enabled || loading) return;
    setLoading("list_proposals");
    setError(null);
    setNotice(null);
    try {
      const response = await onQuery(conversation, "list_proposals", {
        sourceConversationId: conversation.id,
        limit: 30,
      });
      const data = unwrapData(response);
      const items = Array.isArray(data?.items)
        ? data.items.filter(isRecord).map(asProposal).filter(Boolean) as VsmartProposal[]
        : [];
      setProposals(items);
      setProposalsLoaded(true);
    } catch (cause) {
      setError(friendlyError(cause instanceof Error ? cause.message : "PROPOSAL_QUERY_FAILED"));
    } finally {
      setLoading(null);
    }
  }

  async function reviewProposal(
    proposal: VsmartProposal,
    decision: "approved" | "rejected",
    executeAfterApproval: boolean,
  ) {
    if (!enabled || !canReview || loading) return;
    const verb = decision === "approved"
      ? executeAfterApproval ? "aprobar y ejecutar" : "aprobar"
      : "rechazar";
    if (!window.confirm(`¿Confirmas ${verb} esta propuesta?`)) return;

    setLoading(`${decision}:${proposal.id}`);
    setError(null);
    setNotice(null);
    try {
      await onQuery(conversation, "review_proposal", {
        proposalId: proposal.id,
        decision,
        note: reviewNotes[proposal.id]?.trim() || undefined,
      });
      if (decision === "approved" && executeAfterApproval) {
        await onQuery(conversation, "execute_proposal", { proposalId: proposal.id });
        setNotice("Propuesta aprobada y ejecutada. V.Smart registró el antes y el después.");
      } else {
        setNotice(decision === "approved"
          ? "Propuesta aprobada. Quedó pendiente de ejecución controlada."
          : "Propuesta rechazada. No se modificó V.Smart.");
      }
      await refreshProposalsAfterMutation();
    } catch (cause) {
      setError(friendlyError(cause instanceof Error ? cause.message : "PROPOSAL_OPERATION_FAILED"));
    } finally {
      setLoading(null);
    }
  }

  async function executeProposal(proposal: VsmartProposal) {
    if (!enabled || !canReview || loading) return;
    if (!window.confirm("¿Ejecutar ahora el cambio aprobado en V.Smart?")) return;

    setLoading(`execute:${proposal.id}`);
    setError(null);
    setNotice(null);
    try {
      await onQuery(conversation, "execute_proposal", { proposalId: proposal.id });
      setNotice("Cambio ejecutado de forma idempotente y auditada en V.Smart.");
      await refreshProposalsAfterMutation();
    } catch (cause) {
      setError(friendlyError(cause instanceof Error ? cause.message : "PROPOSAL_EXECUTION_FAILED"));
    } finally {
      setLoading(null);
    }
  }

  async function refreshProposalsAfterMutation() {
    const response = await onQuery(conversation, "list_proposals", {
      sourceConversationId: conversation.id,
      limit: 30,
    });
    const data = unwrapData(response);
    const items = Array.isArray(data?.items)
      ? data.items.filter(isRecord).map(asProposal).filter(Boolean) as VsmartProposal[]
      : [];
    setProposals(items);
    setProposalsLoaded(true);
  }

  const data = result ? unwrapData(result.value) : null;

  return (
    <section className="context-card vsmart-context-card">
      <div className="context-card-title">
        <span><Icon name="sparkles" size={17} /> V.Smart Agro</span>
        <small>{enabled ? "Conector firmado · revisión humana" : "Requiere sesión AWS"}</small>
      </div>
      <p className="empty-copy">
        Consulta el estado agrícola y revisa propuestas vinculadas a esta conversación. Ningún cambio se ejecuta sin una decisión humana autorizada.
      </p>
      <div className="context-form-actions vsmart-query-actions">
        <button className="button soft compact" disabled={!enabled || Boolean(loading)} onClick={() => void run("list_context")} type="button">
          {loading === "list_context" ? "Consultando…" : "Contexto"}
        </button>
        <button className="button soft compact" disabled={!enabled || Boolean(loading)} onClick={() => void run("list_tasks")} type="button">
          {loading === "list_tasks" ? "Consultando…" : "Tareas próximas"}
        </button>
        <button className="button soft compact" disabled={!enabled || Boolean(loading)} onClick={() => void run("list_alerts")} type="button">
          {loading === "list_alerts" ? "Consultando…" : "Alertas"}
        </button>
        <button className="button soft compact" disabled={!enabled || Boolean(loading)} onClick={() => void loadProposals()} type="button">
          {loading === "list_proposals" ? "Cargando…" : `Propuestas${proposalsLoaded && pendingCount ? ` (${pendingCount})` : ""}`}
        </button>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      {notice ? <div className="notice vsmart-success"><Icon name="check" size={16} /><span>{notice}</span></div> : null}
      {result && data && !["list_proposals", "review_proposal", "execute_proposal"].includes(result.action)
        ? <VsmartResult action={result.action} data={data} />
        : null}
      {proposalsLoaded ? (
        <ProposalInbox
          canReview={canReview}
          loading={loading}
          notes={reviewNotes}
          onChangeNote={(id, value) => setReviewNotes((current) => ({ ...current, [id]: value }))}
          onExecute={(proposal) => void executeProposal(proposal)}
          onRefresh={() => void loadProposals()}
          onReview={(proposal, decision, executeAfter) => void reviewProposal(proposal, decision, executeAfter)}
          proposals={proposals}
        />
      ) : null}
    </section>
  );
}

function ProposalInbox({
  proposals,
  canReview,
  loading,
  notes,
  onChangeNote,
  onReview,
  onExecute,
  onRefresh,
}: {
  proposals: VsmartProposal[];
  canReview: boolean;
  loading: string | null;
  notes: Record<string, string>;
  onChangeNote: (id: string, value: string) => void;
  onReview: (proposal: VsmartProposal, decision: "approved" | "rejected", executeAfter: boolean) => void;
  onExecute: (proposal: VsmartProposal) => void;
  onRefresh: () => void;
}) {
  return (
    <div className="vsmart-proposal-inbox">
      <div className="vsmart-proposal-heading">
        <div>
          <strong>Propuestas de esta conversación</strong>
          <small>{proposals.length} registradas · cada acción conserva trazabilidad</small>
        </div>
        <button className="context-inline-action" disabled={Boolean(loading)} onClick={onRefresh} type="button">Actualizar</button>
      </div>
      {!proposals.length ? (
        <div className="notice"><Icon name="check" size={16} /><span>No hay propuestas vinculadas a esta conversación.</span></div>
      ) : (
        <div className="vsmart-proposal-list">
          {proposals.map((proposal) => {
            const busy = Boolean(loading?.endsWith(proposal.id));
            const ready = Boolean(proposal.execution?.ready);
            const proposed = proposal.status === "proposed";
            const approved = proposal.status === "approved";
            return (
              <article className={`vsmart-proposal-item status-${proposal.status}`} key={proposal.id}>
                <header>
                  <span className="vsmart-proposal-icon"><Icon name={proposalIcon(proposal.tool_code)} size={15} /></span>
                  <div>
                    <strong>{proposalToolLabel(proposal.tool_code)}</strong>
                    <small>{formatDate(proposal.created_at)} · {requesterLabel(proposal)}</small>
                  </div>
                  <span className={`vsmart-status-chip ${proposal.status}`}>{proposalStatusLabel(proposal.status)}</span>
                </header>
                <p>{proposal.confirmation_text || "Propuesta sin texto de confirmación."}</p>
                {proposal.execution?.blocker ? (
                  <div className="vsmart-blocker"><Icon name="clock" size={14} /><span>{proposalBlocker(proposal.execution.blocker)}</span></div>
                ) : null}
                {proposal.last_execution_error ? (
                  <div className="form-error">Último intento: {friendlyError(proposal.last_execution_error)}</div>
                ) : null}
                {proposal.reviewed_at ? (
                  <small className="vsmart-review-meta">
                    Revisada por {proposal.reviewed_by_email || proposal.reviewed_by_external || "usuario autorizado"} · {formatDate(proposal.reviewed_at)}
                    {proposal.review_note ? ` · ${proposal.review_note}` : ""}
                  </small>
                ) : null}
                {canReview && (proposed || approved) ? (
                  <div className="vsmart-review-controls">
                    {proposed ? (
                      <input
                        maxLength={500}
                        onChange={(event) => onChangeNote(proposal.id, event.target.value)}
                        placeholder="Nota de revisión opcional"
                        value={notes[proposal.id] ?? ""}
                      />
                    ) : null}
                    <div className="context-form-actions">
                      {proposed ? (
                        <button className="button soft compact danger" disabled={busy || Boolean(loading)} onClick={() => onReview(proposal, "rejected", false)} type="button">
                          Rechazar
                        </button>
                      ) : null}
                      {proposed ? (
                        <button className="button primary compact" disabled={busy || Boolean(loading)} onClick={() => onReview(proposal, "approved", ready)} type="button">
                          {ready ? "Aprobar y ejecutar" : "Aprobar"}
                        </button>
                      ) : null}
                      {approved && ready ? (
                        <button className="button primary compact" disabled={busy || Boolean(loading)} onClick={() => onExecute(proposal)} type="button">
                          Ejecutar cambio
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
      {!canReview && proposals.some((item) => item.status === "proposed" || item.status === "approved") ? (
        <p className="empty-copy">Tu rol puede revisar la evidencia, pero la aprobación requiere Gerencia, Operaciones, Supervisor o Administrador.</p>
      ) : null}
    </div>
  );
}

function VsmartResult({ action, data }: { action: VsmartAction; data: Record<string, unknown> }) {
  if (action === "list_context") {
    return (
      <div className="import-stats vsmart-stats">
        <article><strong>{arrayLength(data.farms)}</strong><span>campos</span></article>
        <article><strong>{arrayLength(data.blocks)}</strong><span>cuarteles</span></article>
        <article><strong>{arrayLength(data.applicationPrograms)}</strong><span>programas</span></article>
      </div>
    );
  }

  const items = Array.isArray(data.items)
    ? data.items.filter(isRecord).slice(0, 5)
    : [];

  if (!items.length) {
    return <div className="notice"><Icon name="check" size={16} /><span>{action === "list_alerts" ? "No hay alertas en los filtros actuales." : "No hay tareas dentro de la ventana consultada."}</span></div>;
  }

  return (
    <div className="commitment-list vsmart-result-list">
      {items.map((item, index) => (
        <article className="commitment-item" key={String(item.id ?? index)}>
          <span className={`commitment-check ${action === "list_alerts" ? "pending" : "done"}`}><Icon name={action === "list_alerts" ? "bell" : "check"} size={14} /></span>
          <div>
            <strong>{resultTitle(action, item)}</strong>
            <small>{resultMeta(action, item)}</small>
          </div>
        </article>
      ))}
    </div>
  );
}

function unwrapData(value: QueryResult) {
  const direct = isRecord(value.data) ? value.data : value;
  return isRecord(direct) ? direct : null;
}

function asProposal(value: Record<string, unknown>): VsmartProposal | null {
  if (typeof value.id !== "string" || typeof value.tool_code !== "string" || typeof value.status !== "string") return null;
  return value as unknown as VsmartProposal;
}

function requesterLabel(proposal: VsmartProposal) {
  const requested = proposal.requested_by;
  if (!requested) return "Origen Conversa Smart";
  return String(requested.email || requested.userId || requested.role || "Origen Conversa Smart");
}

function proposalToolLabel(code: string) {
  const labels: Record<string, string> = {
    propose_task_reschedule: "Reprogramación de tarea",
    propose_alert: "Nueva alerta",
    propose_feedback: "Feedback estructurado",
    propose_phenology_observation: "Observación fenológica",
  };
  return labels[code] ?? code;
}

function proposalIcon(code: string): "clock" | "bell" | "document" | "sparkles" {
  if (code === "propose_task_reschedule") return "clock";
  if (code === "propose_alert") return "bell";
  if (code === "propose_feedback") return "document";
  return "sparkles";
}

function proposalStatusLabel(status: VsmartProposal["status"]) {
  const labels: Record<VsmartProposal["status"], string> = {
    proposed: "Pendiente",
    approved: "Aprobada",
    rejected: "Rechazada",
    executed: "Ejecutada",
    cancelled: "Cancelada",
  };
  return labels[status];
}

function proposalBlocker(code: string) {
  const labels: Record<string, string> = {
    SAMPLING_VISIT_REQUIRED: "Requiere seleccionar una visita de muestreo válida antes de ejecutarse.",
    TENANT_PHENOLOGY_STAGE_REQUIRED: "La etapa debe estar configurada en el catálogo fenológico del cliente.",
    PHENOLOGY_CONTEXT_INCOMPLETE: "Falta completar el cuartel o la etapa fenológica.",
    PROPOSAL_TOOL_NOT_EXECUTABLE: "Esta propuesta requiere ejecución manual en V.Smart.",
  };
  return labels[code] ?? code;
}

function resultTitle(action: VsmartAction, item: Record<string, unknown>) {
  if (action === "list_alerts") return String(item.title ?? item.type ?? "Alerta");
  return [item.farmName, item.blockName, item.productName]
    .filter(Boolean)
    .map(String)
    .join(" · ") || "Tarea agrícola";
}

function resultMeta(action: VsmartAction, item: Record<string, unknown>) {
  if (action === "list_alerts") {
    return [item.severity, item.status, item.created_at]
      .filter(Boolean)
      .map(String)
      .join(" · ");
  }
  return [item.scheduled_date, item.time_window_start, item.status, item.phenologicalStageName]
    .filter(Boolean)
    .map(String)
    .join(" · ");
}

function arrayLength(value: unknown) {
  return Array.isArray(value) ? value.length : 0;
}

function formatDate(value?: string | null) {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("es-CL", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function friendlyError(value: string) {
  const labels: Record<string, string> = {
    VSMART_SIGNING_KEY_NOT_CONFIGURED: "La identidad segura de V.Smart todavía no está configurada.",
    INTEGRATION_NOT_TRUSTED: "V.Smart no reconoce todavía esta conexión.",
    TOOL_NOT_ALLOWED: "Esta organización no autorizó esa operación.",
    VSMART_QUERY_FAILED: "V.Smart no pudo completar la consulta.",
    VSMART_ORGANIZATION_NOT_LINKED: "Esta organización de Conversa Smart no está enlazada a V.Smart.",
    PROPOSAL_QUERY_FAILED: "No fue posible cargar las propuestas de V.Smart.",
    PROPOSAL_NOT_FOUND: "La propuesta ya no existe o pertenece a otro tenant.",
    PROPOSAL_ALREADY_REVIEWED: "La propuesta ya fue revisada por otra persona.",
    PROPOSAL_NOT_APPROVED: "La propuesta debe aprobarse antes de ejecutarse.",
    PROPOSAL_REVIEW_ROLE_NOT_ALLOWED: "Tu rol no está autorizado para aprobar o rechazar propuestas.",
    PROPOSAL_EXECUTION_ROLE_NOT_ALLOWED: "Tu rol no está autorizado para ejecutar cambios.",
    TASK_STATE_CHANGED: "La tarea cambió desde que se creó la propuesta. Revísala nuevamente.",
    TASK_NOT_RESCHEDULABLE: "La tarea ya no está en un estado que permita reprogramarla.",
    TASK_NOT_FOUND: "La tarea asociada ya no existe.",
    FARM_NOT_FOUND: "El campo asociado ya no existe.",
    BLOCK_NOT_FOUND: "El cuartel asociado ya no existe.",
    SAMPLING_VISIT_REQUIRED: "Falta una visita de muestreo válida.",
    SAMPLING_VISIT_NOT_FOUND: "La visita de muestreo ya no existe.",
    SAMPLING_VISIT_BLOCK_MISMATCH: "La visita de muestreo no corresponde al cuartel indicado.",
    TENANT_PHENOLOGY_STAGE_REQUIRED: "La etapa fenológica debe pertenecer al catálogo del cliente.",
    PHENOLOGY_STAGE_NOT_FOUND: "La etapa fenológica ya no existe.",
    PROPOSAL_TOOL_NOT_EXECUTABLE: "Esta propuesta requiere una gestión manual en V.Smart.",
    PROPOSAL_EXECUTION_FAILED: "V.Smart no pudo ejecutar la propuesta; no se aplicaron cambios parciales.",
    PROPOSAL_OPERATION_FAILED: "V.Smart no pudo completar la operación sobre la propuesta.",
  };
  return labels[value] ?? value;
}
